import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, BookOpen, Trash2, Loader2, FileText, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BooksManager() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [totalChunks, setTotalChunks] = useState(0);
  const fileInputRef = useRef(null);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pragya/books/`);
      setBooks(res.data?.books || []);
      setTotalChunks(res.data?.total_chunks || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error('फ़ाइल बहुत बड़ी है (अधिकतम 30MB)');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', file.name.replace(/\.(pdf|txt)$/i, ''));
    setUploading(true);
    try {
      const res = await axios.post(`${API}/pragya/books/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`"${res.data.title}" अपलोड हुई (${res.data.chunk_count} खण्ड)`);
      loadBooks();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || 'अपलोड विफल';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (book) => {
    if (!window.confirm(`"${book.title}" को हटाएं?`)) return;
    try {
      await axios.delete(`${API}/pragya/books/${book.book_id}`);
      toast.success('हटाई गई।');
      loadBooks();
    } catch (e) {
      toast.error('हटाने में त्रुटि।');
    }
  };

  const fmtSize = (chars) => {
    if (chars > 1000000) return `${(chars / 1000000).toFixed(1)}M`;
    if (chars > 1000) return `${(chars / 1000).toFixed(0)}K`;
    return `${chars}`;
  };

  return (
    <section className="bg-white rounded-2xl border border-orange-100 p-5">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <h3 className="font-bold text-orange-950 devanagari-ui flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-600" />
            AWGP पुस्तक भण्डार (प्रज्ञा AI)
          </h3>
          <p className="text-xs text-stone-500 mt-1 devanagari-ui">
            पंडित श्रीराम शर्मा आचार्य जी / AWGP पुस्तकें (PDF/TXT) अपलोड करें — प्रज्ञा AI इन्हीं से उत्तर देगी
          </p>
        </div>
        {books.length > 0 && (
          <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full devanagari-ui shrink-0">
            <CheckCircle2 className="w-3 h-3 inline mr-1" />
            {books.length} पुस्तक · {totalChunks} खण्ड
          </span>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt"
        className="hidden"
        onChange={(e) => handleUpload(e.target.files?.[0])}
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 rounded-lg text-orange-700 text-sm transition-colors devanagari-ui disabled:opacity-50"
      >
        {uploading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> अपलोड हो रही है...</>
        ) : (
          <><Upload className="w-4 h-4" /> पुस्तक अपलोड करें (PDF / TXT, अधिकतम 30MB)</>
        )}
      </button>

      {/* Books list */}
      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-stone-500 text-center py-4 devanagari-ui">लोड हो रहा है...</p>
        ) : books.length === 0 ? (
          <div className="text-center py-6 text-stone-500">
            <FileText className="w-10 h-10 mx-auto text-stone-300 mb-2" />
            <p className="text-sm devanagari-ui">अभी तक कोई पुस्तक अपलोड नहीं की गई</p>
            <p className="text-xs mt-1 devanagari-ui">"गायत्री महाविज्ञान", "साधना से सिद्धि" जैसी पुस्तकें जोड़ें</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {books.map((b) => (
              <li key={b.book_id} className="flex items-center gap-2 p-2.5 bg-orange-50/40 border border-orange-100 rounded-lg">
                <BookOpen className="w-4 h-4 text-orange-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 devanagari-ui truncate">{b.title}</p>
                  <p className="text-[10px] text-stone-500">
                    {b.chunk_count} खण्ड · {fmtSize(b.char_count)} chars · {new Date(b.uploaded_at).toLocaleDateString('hi-IN')}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(b)}
                  className="p-1.5 rounded hover:bg-red-100 text-red-600 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
